import { compose, get, head, isNil, isUndefined, last, map, reject, size, uniq } from 'lodash/fp';
import { pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import { sortByAscending, dummyAmount, multiplyAmount, sumAmounts } from '@mews-commander/utils';
import { NoCommonBill, ProductOrder, ProductOrderGroup, ProductOrdersTotal } from './types';

export const createProductOrderGroup = (
    orders: ProductOrder[],
    id = ''
): Omit<ProductOrderGroup, 'ageCategoryId' | 'subgroups'> => {
    const firstOrder = head<ProductOrder>(orders);
    if (isUndefined(firstOrder)) {
        throw new Error('Unexpected empty array');
    }
    const { charging, data } = firstOrder;
    const sortedOrders = sortByAscending<ProductOrder>('consumptionUtc')(orders);
    const nonCanceledOrders = reject('isCanceled', sortedOrders);
    const isWholeGroupCanceled = array.isEmpty(nonCanceledOrders);
    const bill = chooseBill(sortedOrders);
    const consumptionUtc = getGroupConsumptionUtc(nonCanceledOrders);
    const productTotals = pipe(
        sortedOrders,
        array.reduce(
            { totalCost: dummyAmount, totalNumberOfProducts: 0 },
            (acc: ProductOrdersTotal, order: ProductOrder) => {
                const orderSumOfMultilpliers = sumMultipliers(order);
                return {
                    totalCost: sumAmounts(acc.totalCost, multiplyAmount(order.unitAmount, orderSumOfMultilpliers)),
                    totalNumberOfProducts: acc.totalNumberOfProducts + orderSumOfMultilpliers,
                };
            }
        )
    );
    const { totalCost, totalNumberOfProducts } = productTotals;
    return {
        id,
        orders: sortedOrders,
        bill,
        totalNumberOfProducts,
        consumptionUtc,
        totalCost,
        isCanceled: isWholeGroupCanceled,
        productId: data.value.productId,
        charging,
    };
};

const getConsumptionUtc = get('consumptionUtc');

const getGroupConsumptionUtc = (orders: ProductOrder[]) => {
    const firstConsumptionUtc = getConsumptionUtc(head<ProductOrder>(orders));
    const lastConsumptionUtc = getConsumptionUtc(last<ProductOrder>(orders));
    return firstConsumptionUtc && lastConsumptionUtc ? { first: firstConsumptionUtc, last: lastConsumptionUtc } : null;
};

export const sumMultipliers = ({ multipliers: { person, night, unit } }: ProductOrder) => person * night * unit;

type OrderBill = ProductOrder['billId'];
type GroupBill = ProductOrderGroup['bill'];

const getUniqueBills: (orders: ProductOrder[]) => OrderBill[] = compose<ProductOrder[], OrderBill[], OrderBill[]>(
    uniq,
    map('billId')
);

const chooseBill = (orders: ProductOrder[]): GroupBill => {
    const uniqueBills = getUniqueBills(orders);

    if (size(uniqueBills) === 1) {
        const bill = head(uniqueBills);
        if (isNil(bill)) {
            return NoCommonBill.None;
        }
        return bill;
    }
    return NoCommonBill.Various;
};
